import {
  findSavingsWithdrawEntry,
  SAVINGS_RECEIVER_CAPABILITY,
} from '@1delta/calldata-sdk'
import type { Address } from 'viem'
import { MulticallRetryFunction, GenericTokenList } from '../../types'
import { composeVaultDisplayName } from '../displayName'
import { buildReader, type SavingsReader } from './readers'
import { getSavingsRegistry } from './registry'
import { resolveYieldApr } from './yieldBinding'
import { deriveExitRoutes } from '../exitRoutes'
import type { SavingsVault, SavingsVaults } from './types'
import type { VaultExitRoute } from '../exitRoutes'
import type { SavingsOnChainState } from './readers'

const ONE_E18 = 10n ** 18n

/**
 * Does this vault's exit pay anyone other than the caller?
 *
 * The LST provider answers this by PROBING its builders, because its surface
 * is 65 deposit and 50 withdraw kinds and a hand-written `true` there is a
 * claim nobody can keep re-verifying. Savings is the opposite case: it has 13
 * kinds and already carries `SAVINGS_RECEIVER_CAPABILITY`, a `Record` over the
 * union that a new kind cannot ship without declaring. Reading the table is
 * therefore strictly better here than probing — it is the same source the
 * action route already refuses on, so the row and the 400 cannot disagree.
 *
 * Returns `true | undefined`, never `false`, to keep ONE meaning for the field
 * across providers: an LST row can only ever assert self-only or stay silent,
 * so emitting an asserted `false` on savings rows beside them would invite
 * reading the LST silence as a denial. Absent means "not established".
 *
 * A vault with no registry entry is served by the generic ERC-4626 path, whose
 * `redeem(shares, receiver, owner)` is receiver-capable — hence silence rather
 * than `true`.
 */
const resolveSelfOnly = (chainId: string, vault: string): true | undefined => {
  const entry = findSavingsWithdrawEntry(chainId, vault as Address)
  if (!entry) return undefined
  return SAVINGS_RECEIVER_CAPABILITY[entry.kind]?.exit === false
    ? true
    : undefined
}

/** Stamp the row-level receiver fact onto each leg that could carry one. A
 *  `market` route's payout is a trade, not a protocol entrypoint, so the
 *  question does not apply to it. */
const withSelfOnly = (
  routes: VaultExitRoute[],
  selfOnly: true | undefined,
): VaultExitRoute[] =>
  selfOnly === undefined
    ? routes
    : routes.map((r) =>
        r.kind === 'market' || r.selfOnly !== undefined
          ? r
          : { ...r, selfOnly },
      )

/**
 * One-shot fetcher for all savings vaults on a chain.
 *
 * Two parallel I/O legs that both feed the same output map:
 *
 *  1. **On-chain leg** — every reader's calls (3 per ERC-4626 entry:
 *     `totalAssets`, `totalSupply`, `convertToAssets(10**decimals)`;
 *     6 for a Native wNLP) are concatenated into a single
 *     `multicallRetry` payload. The response is sliced back into
 *     per-entry chunks and parsed; per-entry failures are silently
 *     dropped.
 *
 *  2. **APR leg** — `resolveYieldApr` dedups the registry's
 *     `YieldFetcher` refs and runs each once.
 *
 * Returns `{}` when the chain has no registry entries.
 */
/**
 * bfBTC's exits, from what the chain says rather than from the mode name.
 *
 * The generic derivation would publish one `queued` route for every
 * `request-based` row, and on Base and Pharos that route does not exist: BitFi
 * switches the EVM leg off with a 100 % `percentageFee`, and the Bitcoin leg
 * off on Hemi and Bitlayer with a `uint128.max` fixed fee (see the reader).
 * Advertising a route a chain has closed is the failure this file otherwise
 * guards against everywhere — a button that 400s — so the two legs are
 * published from the live flags, each with its own id, and a leg that is off
 * is simply absent.
 *
 * `queued-bitcoin` pays BTC on the Bitcoin network to an address the caller
 * supplies. It is `selfOnly` (the request is keyed to `msg.sender`) and has
 * no on-chain claim — BitFi's Safe pays it out directly. Returns `undefined`
 * for every non-BitFi row so the generic path is untouched; an EMPTY array is
 * a real statement (both legs shut, or paused) and is what `fromVault` turns
 * into `canWithdraw: false`.
 */
export const bitfiExitRoutes = (
  exit: SavingsOnChainState['bitfiExit'],
  waitSeconds: number | undefined,
): VaultExitRoute[] | undefined => {
  if (!exit) return undefined
  const routes: VaultExitRoute[] = []
  if (exit.evmExitEnabled) {
    routes.push({
      id: 'queued',
      kind: 'queued',
      label: 'Queued (multisig-approved)',
      settlement: 'async',
      // The proportional fee is 0 on every open leg; the flat per-request fee
      // (0.000006–0.00009 bfBTC by chain) is stated on the row.
      feeBps: 0,
      waitSeconds,
      description:
        "Burns the bfBTC and books a request that BitFi's Safe must approve before it can be claimed; no on-chain deadline.",
    })
  }
  if (exit.nativeExitEnabled) {
    routes.push({
      id: 'queued-bitcoin',
      kind: 'queued',
      label: 'Bitcoin network',
      settlement: 'async',
      feeBps: 0,
      waitSeconds,
      selfOnly: true,
      description:
        'Pays BTC on the Bitcoin network to an address you supply, settled by BitFi off-chain — there is no on-chain claim and no way to correct a wrong address.',
    })
  }
  return routes
}

export const fetchSavingsVaults = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
): Promise<SavingsVaults> => {
  const entries = getSavingsRegistry(chainId)
  if (entries.length === 0) return {}

  const readers: SavingsReader[] = entries.map(buildReader)
  const allCalls = readers.flatMap((r) => r.calls)
  const allAbis = readers.flatMap((r) => r.abis)

  const [rawResults, aprByAddress] = await Promise.all([
    allCalls.length > 0
      ? multicallRetry({
          chain: chainId,
          calls: allCalls as unknown as never[],
          abi: allAbis as unknown as never,
        })
      : Promise.resolve([] as unknown[]),
    resolveYieldApr(entries),
  ])

  const out: SavingsVaults = {}
  let cursor = 0
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]
    const reader = readers[i]
    const slice = rawResults.slice(cursor, cursor + reader.calls.length)
    cursor += reader.calls.length

    const state = reader.parse(slice)
    if (!state) continue

    const addressLc = entry.address.toLowerCase()
    const underlyingLc = entry.underlying.toLowerCase()
    const asset = tokenList[underlyingLc]
    const priceUsd = prices[underlyingLc]
    // A reader that can derive the rate from the same on-chain state it
    // already read wins over the fetcher leg — see
    // `SavingsOnChainState.supplyRate`. Nearly every entry leaves it absent
    // and falls through to its `yieldFetcher`.
    const supplyRate = state.supplyRate ?? aprByAddress[addressLc] ?? 0
    const rewardsRate = 0
    const depositRate = supplyRate + rewardsRate

    // Share and underlying decimals — yUSD is the lone divergent
    // case today (18-d shares over 6-d USDC). Every other entry
    // collapses both to `entry.decimals`.
    const shareDec = entry.decimals
    const underlyingDec = entry.underlyingDecimals ?? entry.decimals
    const shareUnit = 10n ** BigInt(shareDec)
    const underlyingUnit = 10n ** BigInt(underlyingDec)

    // `totalAssets` is in raw UNDERLYING units (from the vault's
    // `totalAssets()` call), so format with the underlying decimals.
    const totalAssetsFormatted = Number(state.totalAssets) / 10 ** underlyingDec
    const totalAssetsUsd =
      priceUsd !== undefined ? totalAssetsFormatted * priceUsd : 0

    // Withdrawal-mode-aware liquidity — the cross-provider semantic is
    // "what a holder of all shares can pull out this block":
    //  - `instant`       → fully liquid up to totalAssets.
    //  - `fee-or-queued` → the reader's live instant-exit inventory
    //    (Native: the chain's CreditVault balance), clamped to
    //    totalAssets since a pool can never pay out more than it holds.
    //    Zero when the protocol has the instant leg switched off.
    //  - everything else → 0; a waiting period stands in the way.
    const isInstant = entry.withdrawalMode === 'instant'
    let liquidityAmount = isInstant ? state.totalAssets : 0n
    if (!isInstant && state.instantRedeemCapacity !== undefined) {
      liquidityAmount =
        state.instantRedeemEnabled === false
          ? 0n
          : state.instantRedeemCapacity < state.totalAssets
            ? state.instantRedeemCapacity
            : state.totalAssets
    }
    const liquidityRaw = liquidityAmount.toString()
    const liquidityFormatted = Number(liquidityAmount) / 10 ** underlyingDec
    const liquidityUsd =
      priceUsd !== undefined ? liquidityFormatted * priceUsd : 0

    // Lockup indicator — see `SavingsVault.instantLiquidityRatio`. Computed
    // in integer space before the float divide so a huge 18-decimal pool
    // can't lose precision, and clamped because a protocol's exit buffer
    // can exceed the pool it serves (Native's CreditVault also custodies
    // market-maker collateral). An empty vault has nothing to lock up.
    const instantLiquidityRatio =
      state.totalAssets > 0n
        ? Math.min(
            1,
            Number((liquidityAmount * 1_000_000n) / state.totalAssets) / 1e6,
          )
        : 1

    // ERC-4626 semantics:
    //   convertToAssets(shareUnit raw shares) → raw underlying
    //     = exchangeRate * underlyingUnit / 1e18
    //   convertToShares(underlyingUnit raw underlying) → raw shares
    //     = 1e18 * shareUnit / exchangeRate
    const convertToAssets = (state.exchangeRate * underlyingUnit) / ONE_E18
    // A reader that can read the vault's ENTRY price publishes it (Saturn's
    // `previewDeposit` is net of an admin fee dial that `convertToAssets` does
    // not see); everything else derives it from the redeemable rate, which is
    // exact whenever the vault mints at par.
    const convertToShares =
      state.convertToShares ??
      (state.exchangeRate > 0n
        ? (ONE_E18 * shareUnit) / state.exchangeRate
        : 0n)

    // Entry-side room, for the readers whose protocol caps deposits by
    // something no `maxDeposit()` reports. `undefined` (uncapped) and
    // `0n` (full) are different answers — never collapse them, so the
    // whole trio stays absent rather than defaulting to zero.
    const depositCapacity = state.depositCapacity?.toString()
    const depositCapacityFormatted =
      state.depositCapacity !== undefined
        ? Number(state.depositCapacity) / 10 ** underlyingDec
        : undefined
    const depositCapacityUsd =
      depositCapacityFormatted !== undefined && priceUsd !== undefined
        ? depositCapacityFormatted * priceUsd
        : undefined

    // Signed redeemable-vs-fundamental gap; see
    // `SavingsVault.redemptionDiscountBps`. Computed in integer space so
    // a 1e18-scaled rate can't lose the basis point in a float divide.
    const redemptionDiscountBps =
      state.fundamentalExchangeRate !== undefined &&
      state.fundamentalExchangeRate > 0n
        ? Number(
            (state.exchangeRate * 100_000_000n) /
              state.fundamentalExchangeRate -
              100_000_000n,
          ) / 10_000
        : undefined

    // `label` exists for the rows whose `symbol` is the UNDERLYING's ticker
    // because the position is not a token (Frankencoin's savings module).
    // Naming those from the symbol names them after the money they hold, and
    // on Ethereum it collided head-on with the svZCHF wrapper beside them.
    const productName = entry.label ?? entry.symbol

    // `composeVaultDisplayName` builds "<brand> <underlying ticker>", which is
    // right for a share token whose symbol is a decoration of the underlying
    // and wrong for a labelled row — that label exists precisely because the
    // underlying's ticker is not what the product is called.
    const displayName = entry.label
      ? `${entry.brand} ${entry.label}`
      : composeVaultDisplayName(
          entry.brand,
          entry.brand,
          asset ?? {
            chainId,
            address: underlyingLc,
            symbol: entry.symbol,
            decimals: entry.decimals,
          },
          `${entry.brand} ${productName}`,
        )

    const vault: SavingsVault = {
      address: addressLc,
      underlying: underlyingLc,
      symbol: entry.symbol,
      name: `${entry.brand} ${productName}`,
      displayName,
      brand: entry.brand,
      curatorName: entry.brand,
      description: entry.description,
      decimals: entry.decimals,
      assetDecimals: underlyingDec,
      totalAssets: state.totalAssets.toString(),
      totalSupply: state.totalSupply.toString(),
      convertToShares: convertToShares.toString(),
      convertToAssets: convertToAssets.toString(),
      exchangeRate: state.exchangeRate.toString(),
      supplyRate,
      rewardsRate,
      depositRate,
      // NOT part of `depositRate` — it is the incentive the vault's position
      // earns and the depositor does not. See the field's docstring.
      strandedRewardsRate: state.strandedRewardsRate,
      // Opt-IN, not opt-out: a new bespoke reader exists precisely
      // because its token is not a conforming vault, so the default for
      // an unlisted reader must be `false`. `erc4626-idle`,
      // `erc4626-withdraw-limit` and `erc4626-cooldown` are the plain 4626
      // surface plus one extra read (an inventory balance / the strategy's
      // limit view / the exit lock), so they count; Native's wNLP and Re's
      // NAV-oracle tokens revert on
      // `asset()`/`totalAssets()`/`convertToAssets()` and do not.
      isErc4626:
        entry.reader === undefined ||
        entry.reader === 'erc4626' ||
        entry.reader === 'erc4626-cooldown' ||
        entry.reader === 'erc4626-idle' ||
        entry.reader === 'erc4626-withdraw-limit' ||
        // Venus's Liquidity Hub IS a conforming 4626 on every leg
        // (`deposit(assets, receiver)`, `redeem(shares, receiver, owner)`);
        // the bespoke reader exists for the FRV lock, the rate blend and the
        // emission read, not because the standard surface is missing.
        entry.reader === 'venus-hub' ||
        // hbfUSD / pbfUSD ARE conforming 4626 — the bespoke reader exists for
        // the epoch ledger and the share-denominated supply cap, not because
        // the standard surface is missing. bfBTC (`bitfi-bfbtc`) is the
        // opposite and correctly falls through to `false`.
        entry.reader === 'bitfi-vault',
      isRebasing: entry.isRebasing,
      isMintable: entry.isMintable,
      mintContract: entry.mintContract?.toLowerCase() ?? addressLc,
      // Only emitted when true — an absent field is the ordinary vault, and
      // `false` on 200 rows would read as a claim nobody made.
      ...(entry.secondaryMarketOnly ? { secondaryMarketOnly: true } : {}),
      withdrawalMode: entry.withdrawalMode,
      // On-chain wins over the registry's pinned fallback — Native's
      // queue window is per-asset and governance-mutable.
      withdrawalCooldownSeconds:
        state.withdrawalCooldownSeconds ?? entry.withdrawalCooldownSeconds,
      withdrawFeeBps: state.withdrawFeeBps,
      depositFeeBps: state.depositFeeBps,
      // Term-sheet inputs that no reader can derive — see the registry's
      // docstrings. Passed through verbatim so the sheet can stop hardcoding
      // "compounds per second" and "needs an approval" for every vault.
      yieldWarmupSeconds: entry.yieldWarmupSeconds,
      accrual: entry.accrual,
      needsDepositApproval: entry.needsDepositApproval,
      // Drives the USER-balance read. Absent ⇒ `balanceOf` on the share
      // token; `savings-account` is the one address that is not a token.
      balanceKind: entry.balanceKind,
      instantRedeemEnabled: state.instantRedeemEnabled,
      inventoryContract: entry.inventoryContract?.toLowerCase(),
      withdrawQueue: state.withdrawQueue ?? entry.withdrawQueue?.toLowerCase(),
      // Both legs of a two-legged exit, spelled out — see `VaultExitRoute`.
      // Derived from the flat fields the readers already establish, so every
      // savings row gets it without a per-provider change; the queued leg's
      // fee is left ABSENT rather than 0 because "not published" and "free"
      // are different claims and only some protocols make the second.
      exitRoutes: withSelfOnly(
        bitfiExitRoutes(
          state.bitfiExit,
          state.withdrawalCooldownSeconds ?? entry.withdrawalCooldownSeconds,
        ) ??
          deriveExitRoutes({
            withdrawalMode: entry.withdrawalMode,
            withdrawFeeBps: state.withdrawFeeBps,
            withdrawalCooldownSeconds:
              state.withdrawalCooldownSeconds ??
              entry.withdrawalCooldownSeconds,
            liquidity: liquidityRaw,
            liquidityFormatted,
            liquidityUsd,
            // The floor that decides whether a single-route exit is reachable.
            exitMinAmount: state.exitMinAmount?.toString(),
            exitMinAmountFormatted:
              state.exitMinAmount !== undefined
                ? Number(state.exitMinAmount) / 10 ** underlyingDec
                : undefined,
            // Adds the `market` leg — the only route a holder without the
            // protocol's mint/redeem permission actually has.
            secondaryMarketOnly: entry.secondaryMarketOnly,
          }),
        resolveSelfOnly(chainId, entry.address),
      ),
      depositCapacity,
      depositCapacityFormatted,
      depositCapacityUsd,
      redemptionDiscountBps,
      solvency: entry.solvency,
      // Only the NAV-oracle reader's entries publish their share price from a
      // feed; on every other entry `priceOracle` is unset, so this stays absent
      // rather than pointing at an unrelated contract.
      navOracle:
        entry.reader === 'nav-oracle'
          ? entry.priceOracle?.toLowerCase()
          : undefined,
      asset,
      priceUsd,
      totalAssetsFormatted,
      totalAssetsUsd,
      liquidity: liquidityRaw,
      liquidityFormatted,
      liquidityUsd,
      instantLiquidityRatio,
    }
    out[addressLc] = vault
  }

  return out
}
